import {
  DashboardSquare01Icon,
  LinkForwardIcon,
  Refresh01Icon,
  Settings01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { systemSettingsApi } from '@/lib/api-client';

const DEFAULT_URL =
  'https://opexmes-dashboard.dzulfikar-at.workers.dev/?date=last-30-days&dashboard=production-overview&showSidebar=false';

export const Route = createFileRoute('/_authenticated/analytics/custom-dashboard')({
  component: CustomDashboardPage,
});

function CustomDashboardPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [tempUrl, setTempUrl] = useState<string>('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);

  // Fetch the custom dashboard URL from system settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['system-settings', 'analytics'],
    queryFn: () => systemSettingsApi.getByDomain('analytics'),
  });

  // Extract the URL from settings
  const dashboardUrl =
    settings?.find((s) => s.key === 'custom-dashboard-url')?.value || DEFAULT_URL;

  // Update tempUrl when settings load
  useEffect(() => {
    if (dashboardUrl) {
      setTempUrl(dashboardUrl);
    }
  }, [dashboardUrl]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (url: string) => systemSettingsApi.update('analytics', 'custom-dashboard-url', url),
    onSuccess: () => {
      toast.success(
        t('analytics.customDashboard.settings.saved', 'Dashboard URL saved successfully'),
      );
      queryClient.invalidateQueries({ queryKey: ['system-settings', 'analytics'] });
      setIframeKey((prev) => prev + 1); // Refresh iframe
      setIsSettingsOpen(false);
    },
    onError: (error: Error) => {
      toast.error(
        error?.message ||
          t('analytics.customDashboard.settings.saveError', 'Failed to save dashboard URL'),
      );
    },
  });

  const handleSaveUrl = () => {
    if (tempUrl.trim()) {
      updateMutation.mutate(tempUrl.trim());
    }
  };

  const handleResetToDefault = () => {
    setTempUrl(DEFAULT_URL);
  };

  const handleRefresh = () => {
    setIframeKey((prev) => prev + 1);
  };

  const openInNewTab = () => {
    if (dashboardUrl) {
      window.open(dashboardUrl, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 h-[calc(100vh-2rem)]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <HugeiconsIcon icon={DashboardSquare01Icon} className="size-6" />
            {t('analytics.customDashboard.title')}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t('analytics.customDashboard.description')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <HugeiconsIcon icon={Refresh01Icon} className="size-4 mr-1" />
            {t('buttons.refresh')}
          </Button>
          <Button variant="outline" size="sm" onClick={openInNewTab}>
            <HugeiconsIcon icon={LinkForwardIcon} className="size-4 mr-1" />
            {t('buttons.openInNewTab')}
          </Button>
          <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <HugeiconsIcon icon={Settings01Icon} className="size-4 mr-1" />
                {t('nav.settings')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[525px]">
              <DialogHeader>
                <DialogTitle>{t('analytics.customDashboard.settings.title')}</DialogTitle>
                <DialogDescription>
                  {t(
                    'analytics.customDashboard.settings.descriptionTenant',
                    'Configure the URL for your custom dashboard. This setting applies to all users in your organization.',
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="dashboard-url">
                    {t('analytics.customDashboard.settings.urlLabel')}
                  </Label>
                  <Input
                    id="dashboard-url"
                    placeholder="https://example.com/dashboard"
                    value={tempUrl}
                    onChange={(e) => setTempUrl(e.target.value)}
                    className="col-span-3"
                    disabled={updateMutation.isPending}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('analytics.customDashboard.settings.urlHint')}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={handleResetToDefault}
                  disabled={updateMutation.isPending}
                >
                  {t('buttons.resetToDefault')}
                </Button>
                <Button
                  onClick={handleSaveUrl}
                  disabled={updateMutation.isPending || !tempUrl.trim()}
                >
                  {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t('buttons.save')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Dashboard iframe */}
      <Card className="flex-1 overflow-hidden h-[calc(100%-5rem)]">
        <CardContent className="p-0 h-full">
          {dashboardUrl ? (
            <iframe
              key={iframeKey}
              src={dashboardUrl}
              className="w-full h-full border-0"
              title="Custom Dashboard"
              allow="fullscreen"
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <HugeiconsIcon icon={DashboardSquare01Icon} className="size-16 mb-4 opacity-50" />
              <p>{t('analytics.customDashboard.noUrl')}</p>
              <Button variant="link" onClick={() => setIsSettingsOpen(true)}>
                {t('analytics.customDashboard.configure')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
